import type {
  BootstrapData,
  CustomerVerificationCall,
  InventoryIntegration,
  IssuedCustomerVerification,
  MenuItem,
  Order,
  Restaurant,
  StaffProfile,
  StaffRole,
} from "./types";

const defaultApiUrl = "https://butrufdcduurydoafqia.supabase.co/functions/v1/nuviway-api";
const apiBaseUrl = (import.meta.env.VITE_API_URL || defaultApiUrl).replace(/\/$/, "");

export class CenterApiError extends Error {
  constructor(message: string, readonly code: string, readonly status: number) {
    super(message);
    this.name = "CenterApiError";
  }
}

async function readResult<T>(response: Response): Promise<T> {
  const body = await response.json().catch(() => ({})) as { error?: { code?: string; message?: string } } & T;
  if (!response.ok) {
    throw new CenterApiError(
      body.error?.message ?? "تعذر الاتصال بالخدمة",
      body.error?.code ?? "API_ERROR",
      response.status,
    );
  }
  return body;
}

export function createCenterApi(tokenProvider: () => string) {
  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = tokenProvider();
    const response = await fetch(`${apiBaseUrl}${path}`, {
      ...init,
      headers: {
        Accept: "application/json",
        ...(init.body && typeof init.body === "string" ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init.headers,
      },
    });
    return await readResult<T>(response);
  }

  return {
    async login(username: string, password: string) {
      return await request<{ token: string; role: string; subjectId: string }>("/v1/auth/center/login", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });
    },
    async changePassword(currentPassword: string, newPassword: string) {
      return await request<{ token: string; role: string; subjectId: string }>(
        "/v1/auth/center/password",
        { method: "POST", body: JSON.stringify({ currentPassword, newPassword }) },
      );
    },
    async logout() {
      await request<{ loggedOut: true }>("/v1/auth/logout", { method: "POST" });
    },
    async session() {
      return await request<{ session: { subjectId: string; role: string; restaurantId: string | null } }>("/v1/auth/session");
    },
    async bootstrap() {
      return await request<BootstrapData>("/v1/dispatcher/bootstrap");
    },
    async orders() {
      return (await request<{ orders: Order[] }>("/v1/dispatcher/orders?limit=100")).orders;
    },
    async phoneVerifications() {
      return (await request<{ requests: CustomerVerificationCall[] }>(
        "/v1/dispatcher/phone-verifications",
      )).requests;
    },
    async issuePhoneVerificationCode(challengeId: string) {
      return (await request<{ verification: IssuedCustomerVerification }>(
        `/v1/dispatcher/phone-verifications/${encodeURIComponent(challengeId)}/code`,
        { method: "POST", body: JSON.stringify({}) },
      )).verification;
    },
    async saveRestaurant(restaurant: Omit<Restaurant, "updatedAt">) {
      return (await request<{ restaurant: Restaurant }>("/v1/dispatcher/restaurants", {
        method: "POST",
        body: JSON.stringify(restaurant),
      })).restaurant;
    },
    async setRestaurantActive(id: string, isActive: boolean) {
      return (await request<{ restaurant: Restaurant }>(`/v1/dispatcher/restaurants/${encodeURIComponent(id)}/active`, {
        method: "POST",
        body: JSON.stringify({ isActive }),
      })).restaurant;
    },
    async saveMenuItem(item: Pick<
      MenuItem,
      | "restaurantId"
      | "id"
      | "name"
      | "description"
      | "price"
      | "imageUrl"
      | "externalSku"
      | "isAvailable"
      | "sortOrder"
    >) {
      return (await request<{ item: MenuItem }>("/v1/dispatcher/menu-items", {
        method: "POST",
        body: JSON.stringify(item),
      })).item;
    },
    async setItemAvailability(restaurantId: string, itemId: string, isAvailable: boolean) {
      return (await request<{ item: MenuItem }>(
        `/v1/dispatcher/menu-items/${encodeURIComponent(restaurantId)}/${encodeURIComponent(itemId)}/availability`,
        { method: "POST", body: JSON.stringify({ isAvailable }) },
      )).item;
    },
    async issueInventoryIntegration(restaurantId: string, name: string) {
      return await request<{ integration: InventoryIntegration; token: string }>(
        "/v1/dispatcher/inventory-integrations",
        { method: "POST", body: JSON.stringify({ restaurantId, name }) },
      );
    },
    async revokeInventoryIntegration(id: string) {
      return (await request<{ integration: InventoryIntegration }>(
        `/v1/dispatcher/inventory-integrations/${encodeURIComponent(id)}/revoke`,
        { method: "POST", body: JSON.stringify({}) },
      )).integration;
    },
    async uploadProductImage(file: File, restaurantId: string, itemId: string) {
      return await request<{ imageUrl: string }>(
        `/v1/dispatcher/media/product-image?restaurantId=${encodeURIComponent(restaurantId)}&itemId=${encodeURIComponent(itemId)}`,
        { method: "POST", headers: { "Content-Type": file.type }, body: file },
      );
    },
    async deleteProductImage(imageUrl: string) {
      await request<{ deleted: true }>("/v1/dispatcher/media/product-image/delete", {
        method: "POST",
        body: JSON.stringify({ imageUrl }),
      });
    },
    async issueActivation(input: {
      role: StaffRole;
      subjectId: string;
      displayName: string;
      phone: string;
      restaurantId: string | null;
      validForHours: number;
    }) {
      return (await request<{
        activation: { code: string; expiresAt: string; profile: StaffProfile };
      }>("/v1/dispatcher/staff/activation-codes", {
        method: "POST",
        body: JSON.stringify(input),
      })).activation;
    },
    async setStaffActive(subjectId: string, isActive: boolean) {
      return (await request<{ profile: StaffProfile }>(`/v1/dispatcher/staff/${encodeURIComponent(subjectId)}/active`, {
        method: "POST",
        body: JSON.stringify({ isActive }),
      })).profile;
    },
    async assignDriver(orderId: string, driverId: string) {
      return (await request<{ order: Order }>(`/v1/orders/${encodeURIComponent(orderId)}/driver/assign`, {
        method: "POST",
        body: JSON.stringify({ driverId }),
      })).order;
    },
    async confirmPickup(orderId: string, driverId: string, reason: string) {
      return (await request<{ order: Order }>(`/v1/orders/${encodeURIComponent(orderId)}/dispatcher/confirm-pickup`, {
        method: "POST",
        body: JSON.stringify({ driverId, reason }),
      })).order;
    },
    async confirmDelivery(orderId: string, driverId: string, deliveryCode: string, reason: string) {
      return (await request<{ order: Order }>(`/v1/orders/${encodeURIComponent(orderId)}/dispatcher/confirm-delivery`, {
        method: "POST",
        body: JSON.stringify({ driverId, deliveryCode, reason, cashCollected: true }),
      })).order;
    },
  };
}
